import React from "react";
import { useTheme } from "@mui/material";

import { Dialog, IconButton, Typography } from "..";
import useCircleBackgroundRef from "../hooks/useCircleBackgroundRef";
import { formatPercent } from "../utils/formatUtils";
import CloseBig from "@tiny/icons/generated/CloseBig";
import useBreakpoints from "../hooks/useBreakpoints";
import PieChart from "../PieChart";
import { useStyles } from "./PieChartDialog.styles";
import { PieChartDialogProps } from "./PieChartDialog.types";
import ChartTable from "./ChartTable/ChartTable";

function PieChartDialog<T>(props: PieChartDialogProps<T>) {
  const {
    data,
    labelAccessor,
    valueAccessor,
    colorAccessor,
    title,
    labelHeader,
    valueHeader,
    onClose,
    ...other
  } = props;

  const theme = useTheme();

  const { mdUp } = useBreakpoints();

  const classes = useStyles();

  const circleBackgroundRef = useCircleBackgroundRef({
    color: theme.palette.grey[200],
    radius: 255,
    distance: 145,
    total: 6,
  });

  const valuePercentAccessor = (item: T) => {
    const total = data.reduce((acc, x) => acc + valueAccessor(x), 0);
    return formatPercent((valueAccessor(item) * 100) / total);
  };

  return (
    <Dialog {...other} onClose={onClose} fullScreen fullWidth disableScrollLock>
      <div className={classes.root}>
        <div className={classes.header}>
          <Typography
            component="h1"
            variant="h2"
            fontWeight="semiBold"
            m={0}
            className={classes.title}
            color="grey"
            tone={900}
          >
            {title}
          </Typography>
          <IconButton
            size="small"
            onClick={onClose}
            className={classes.closeMobile}
          >
            <CloseBig />
          </IconButton>
        </div>
        <div
          className={classes.chart}
          ref={mdUp ? circleBackgroundRef : undefined}
        >
          <Typography
            variant="h3"
            fontWeight="semiBold"
            className={classes.chartTitle}
            color="grey"
            tone={900}
            m={0}
          >
            Piechart
          </Typography>
          <IconButton
            size="small"
            onClick={onClose}
            className={classes.closeDesktop}
            aria-label="Close"
          >
            <CloseBig />
          </IconButton>
          <PieChart
            width={260}
            data={data}
            colorAccessor={colorAccessor}
            valueAccessor={valueAccessor}
            valueFontSize={32}
            labelFontSize={14}
            ornamentRadius={mdUp ? 100 : 92}
            thickness={mdUp ? 10 : 20}
          />
        </div>
        <div className={classes.list}>
          <div className={classes.listHeader}>
            <Typography
              variant="h3"
              component="h1"
              fontWeight="bold"
              m={0}
              color="grey"
              tone={900}
            >
              {title}
            </Typography>
          </div>
          <ChartTable
            data={data}
            labelAccessor={labelAccessor}
            valueAccessor={valuePercentAccessor}
            colorAccessor={colorAccessor}
            labelHeader={labelHeader}
            valueHeader={valueHeader}
          />
        </div>
      </div>
    </Dialog>
  );
}

export default React.memo(PieChartDialog) as unknown as typeof PieChartDialog;
